import { apiFetch } from "@/lib/api";
import { Card, CardBody, CardHeader, Chip, CircularProgress, Progress } from "@heroui/react";
import { useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface Selo {
  cor_fim: string;
  cor_inicio: string;
  descricao: string;
  icone: string;
  id_selo: string;
  ordem: number;
  slug: string;
  tipo: string;
  titulo: string;
}

interface ProjetoAtivo {
  data_fim: string;
  data_inicio: string;
  descricao: string;
  habilitado: boolean;
  id_participacao: string;
  id_projeto: string;
  id_usuario: string;
  imagem_atividade: string;
  meu_papel: string;
  meu_status: string;
  percentual_conclusao: number;
  selos: Selo[];
  status: string;
  tags: string[];
  texto: string;
  titulo: string;
  xp_conclusao: number;
}

export default function ActiveProjects() {
  const router = useRouter();
  const { data: session } = useSession();
  const pathname = usePathname();
  const [projetos, setProjetos] = useState<ProjetoAtivo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProjetosAtivos = async () => {
    if (!session?.accessToken) return;

    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch("/projetos?me=true", {
        method: "GET",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${session.accessToken}`,
        },
      });

      if (!res.ok) {
        const body = await res.text();
        console.error("Erro ao buscar projetos ativos:", body);
        setError("Não foi possível carregar seus projetos ativos.");
        return;
      }

      const data: ProjetoAtivo[] = await res.json();
      setProjetos(data ?? []);
    } catch (err) {
      console.error(err);
      setError("Ocorreu um erro ao carregar seus projetos ativos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      loadProjetosAtivos();
    }
    // se o linter reclamar do loadProjetosAtivos, pode ignorar ou envolver em useCallback
  }, [session]);

  const handleOpenProjeto = (id_projeto: string) => {
    router.push(`${pathname}?modal=viewProject&projectId=${id_projeto}`);
  };

  const progressBarProjetosColors = (percentualConclusao: number) => {
    if (percentualConclusao < 50) {
      return "success" as const;
    } else if (percentualConclusao > 50 && percentualConclusao < 75) {
      return "warning" as const;
    } else if (percentualConclusao > 75) return "danger" as const;

    return "default" as const;
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col gap-1">
        <h2 className="text-lg md:text-xl font-semibold">Projetos ativos</h2>
        <p className="text-xs md:text-sm text-default-500">Projetos em que você está participando no momento</p>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 md:grid md:grid-cols-2">
        {loading && (
          <div className="w-full col-span-2 flex justify-center items-center py-4">
            <CircularProgress />
          </div>
        )}

        {!loading && error && <p className="text-sm text-danger-500 col-span-2">{error}</p>}

        {!loading && !error && projetos.length === 0 && (
          <p className="text-sm text-default-500 col-span-2">Você ainda não está participando de nenhum projeto.</p>
        )}

        {!loading &&
          !error &&
          projetos.length > 0 &&
          projetos.map((projeto) => (
            <button
              key={projeto.id_participacao}
              type="button"
              onClick={() => handleOpenProjeto(projeto.id_projeto)}
              className="text-left border border-default-200 rounded-xl p-3 md:p-4 flex flex-col gap-2 hover:bg-default-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-base md:text-lg">{projeto.titulo || "Projeto sem título"}</h3>
                <Chip className="text-[10px] md:text-xs bg-default-100 text-default-600">{projeto.meu_status || projeto.status || "SEM STATUS"}</Chip>
              </div>

              {projeto.descricao && <p className="text-xs md:text-sm text-default-500 line-clamp-2">{projeto.descricao}</p>}

              {/* Progresso */}
              <div className="flex flex-col gap-1 mt-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-default-500">Progresso da participação</span>
                  <span className="text-[11px] font-medium">{projeto.percentual_conclusao}%</span>
                </div>

                <Progress size="sm" color={progressBarProjetosColors(projeto.percentual_conclusao)} value={projeto.percentual_conclusao} />
              </div>

              {/* Tags */}
              {projeto.tags && projeto.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {projeto.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-default-100 text-default-600">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
      </CardBody>
    </Card>
  );
}
